import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, FileText, Plus, Trash2 } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import {
  fetchBroadcastTemplates,
  createBroadcastTemplate,
  deleteBroadcastTemplate,
  type AdminBroadcastTemplate,
} from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'

const AUDIENCES = ['all', 'active', 'staff'] as const

/** A15.21 — shablonlar kutubxonasi. Qayta-ishlatiladigan e'lon matnlari. */
export function TemplateLibrary() {
  const t = useT()
  const { toast } = useToast()
  const [showForm, setShowForm] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [form, setForm] = useState({ name: '', body: '', audience: 'all' })
  const [busy, setBusy] = useState(false)

  const submit = async () => {
    if (form.body.trim().length < 1) return
    setBusy(true)
    try {
      await createBroadcastTemplate({
        ...(form.name.trim() ? { name: form.name.trim() } : {}),
        body: form.body.trim(),
        audience: form.audience,
      })
      toast(t.broadcast.tplSaved, { tone: 'success' })
      setForm({ name: '', body: '', audience: 'all' })
      setShowForm(false)
      setReloadKey((n) => n + 1)
    } catch (e) {
      toast(t.broadcast.createFailed, {
        tone: 'error',
        ...(e instanceof Error && e.message ? { description: e.message } : {}),
      })
    } finally {
      setBusy(false)
    }
  }

  const rowActions = useMemo<RowAction<AdminBroadcastTemplate>[]>(
    () => [
      {
        id: 'delete',
        label: t.common.delete,
        icon: <Trash2 size={13} />,
        onSelect: async (r) => {
          try {
            await deleteBroadcastTemplate(r.id)
            toast(t.broadcast.tplDeleted, { tone: 'success' })
            setReloadKey((n) => n + 1)
          } catch {
            toast(t.broadcast.actionFailed, { tone: 'error' })
          }
        },
      },
    ],
    [t, toast],
  )

  const columns = useMemo<AdminColumn<AdminBroadcastTemplate>[]>(
    () => [
      { key: 'id', header: t.col.id, type: 'id', width: 70 },
      { key: 'name', header: t.broadcast.tplName, type: 'text', minWidth: 160 },
      { key: 'body', header: t.broadcast.bodyField, type: 'longtext', minWidth: 260 },
      {
        key: 'audience',
        header: t.broadcast.audience,
        type: 'enum',
        width: 120,
        options: AUDIENCES.map((a) => ({ value: a, label: t.broadcast[a], status: 'review' })),
      },
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchBroadcastTemplates, [])

  return (
    <>
      <PageHeader
        title={t.broadcast.templates}
        screenId="A15.21"
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/broadcasts"
              className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <ChevronLeft size={13} />
              {t.broadcast.title}
            </Link>
            <button
              onClick={() => setShowForm((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md border border-accent bg-accent-subtle px-2.5 py-1 text-sm text-accent hover:bg-accent hover:text-white"
            >
              <Plus size={13} />
              {t.broadcast.tplNew}
            </button>
          </div>
        }
      />
      {showForm && (
        <div className="flex flex-col gap-2 border-b border-border-subtle bg-surface-2 p-3">
          <div className="flex flex-wrap items-center gap-2">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t.broadcast.tplName}
              className="min-w-40 flex-1 rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
            />
            <select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value })}
              className="rounded-md border border-border-default bg-surface-1 px-2 py-1.5 text-sm text-primary"
            >
              {AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {t.broadcast[a]}
                </option>
              ))}
            </select>
          </div>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder={t.broadcast.bodyField}
            rows={3}
            className="rounded-md border border-border-default bg-surface-1 px-2.5 py-1.5 text-sm text-primary"
          />
          <div className="flex justify-end">
            <button
              disabled={busy || form.body.trim().length < 1}
              onClick={submit}
              className="rounded-md border border-accent bg-accent-subtle px-3 py-1.5 text-sm text-accent hover:bg-accent hover:text-white disabled:opacity-50"
            >
              {t.common.save}
            </button>
          </div>
        </div>
      )}
      <DataTable<AdminBroadcastTemplate>
        key={reloadKey}
        screenId="A15.21"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <FileText size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
